import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Loader2, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  oauthAppsApi,
  type AuthProvider,
  type OauthApp,
} from "@/lib/oauth-apps-api";

export function OauthAppsPage() {
  const qc = useQueryClient();
  const apps = useQuery({
    queryKey: ["oauth-apps"],
    queryFn: () => oauthAppsApi.list(),
  });
  const providers = useQuery({
    queryKey: ["oauth-apps", "providers"],
    queryFn: () => oauthAppsApi.providers(),
    staleTime: Infinity,
  });

  const savedProviders = useMemo(
    () => new Set((apps.data ?? []).map((a) => a.provider)),
    [apps.data],
  );
  const availableProviders = useMemo(
    () => (providers.data ?? []).filter((p) => !savedProviders.has(p.key)),
    [providers.data, savedProviders],
  );
  const providersByKey = useMemo(
    () => new Map((providers.data ?? []).map((p) => [p.key, p])),
    [providers.data],
  );
  const callbackUrl = providers.data?.[0]?.callbackUrl ?? "";

  const [createOpen, setCreateOpen] = useState(false);

  if (apps.isPending || providers.isPending) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 p-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">OAuth apps</h1>
          <p className="text-sm text-muted-foreground">
            Per-project OAuth client IDs + secrets you've registered upstream.
            One app per provider powers every integration on that provider —
            Gmail, Calendar, Drive, … all ride your Google app.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          disabled={availableProviders.length === 0}
        >
          <Plus className="mr-2 h-4 w-4" /> New OAuth app
        </Button>
      </header>

      {apps.data && apps.data.length > 0 ? (
        <ul className="space-y-3">
          {apps.data.map((app) => (
            <li key={app.id}>
              <SavedAppCard
                app={app}
                provider={providersByKey.get(app.provider)}
                onDelete={async () => {
                  await oauthAppsApi.remove(app.id);
                  qc.invalidateQueries({ queryKey: ["oauth-apps"] });
                }}
              />
            </li>
          ))}
        </ul>
      ) : (
        <div className="rounded-xl border border-dashed bg-muted/20 p-10 text-center">
          <KeyRound className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No OAuth apps yet. Add one and the BYO option on the Credentials
            page will start using it.
          </p>
        </div>
      )}

      <CreateAppDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        availableProviders={availableProviders}
        callbackUrl={callbackUrl}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ["oauth-apps"] });
        }}
      />
    </div>
  );
}

function SavedAppCard({
  app,
  provider,
  onDelete,
}: {
  app: OauthApp;
  provider?: AuthProvider;
  onDelete: () => Promise<void>;
}) {
  const [deleting, setDeleting] = useState(false);
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div className="flex items-start gap-3">
          {provider && (
            <ProviderLogo provider={provider} size={36} />
          )}
          <div>
            <CardTitle className="text-base">{app.name}</CardTitle>
            <CardDescription className="flex flex-wrap items-center gap-2 pt-1">
              <Badge variant="outline" className="font-normal">
                {provider?.name ?? app.provider}
              </Badge>
              <code className="font-mono text-xs">{app.clientIdPreview}</code>
            </CardDescription>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={async () => {
            setDeleting(true);
            try {
              await onDelete();
            } finally {
              setDeleting(false);
            }
          }}
          disabled={deleting}
          aria-label="Delete OAuth app"
        >
          <Trash2 className="h-4 w-4 text-destructive" />
        </Button>
      </CardHeader>
      <Separator />
      <CardContent className="space-y-2 pt-4">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Authorized redirect URI
        </div>
        <CallbackUrl url={app.callbackUrl} />
      </CardContent>
    </Card>
  );
}

function ProviderLogo({
  provider,
  size = 24,
}: {
  provider: AuthProvider;
  size?: number;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span
        style={{ width: size, height: size }}
        className="inline-flex shrink-0 items-center justify-center rounded-md bg-muted text-[10px] font-bold text-muted-foreground"
      >
        {provider.name[0]?.toUpperCase() ?? "?"}
      </span>
    );
  }
  return (
    <img
      src={provider.logo}
      alt={`${provider.name} logo`}
      width={size}
      height={size}
      onError={() => setFailed(true)}
      className="shrink-0 object-contain"
      style={{ width: size, height: size }}
    />
  );
}

function CallbackUrl({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 truncate rounded bg-background px-2 py-1.5 font-mono text-xs">
        {url}
      </code>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            /* ignore */
          }
        }}
      >
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

function CreateAppDialog({
  open,
  onOpenChange,
  availableProviders,
  callbackUrl,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  availableProviders: AuthProvider[];
  callbackUrl: string;
  onCreated: () => void;
}) {
  const defaultProvider = availableProviders[0]?.key ?? "";

  const [provider, setProvider] = useState<string>(defaultProvider);
  const [name, setName] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setProvider(defaultProvider);
    setName("");
    setClientId("");
    setClientSecret("");
    setError(null);
  };

  const selectedProvider = availableProviders.find((p) => p.key === provider);

  const save = useMutation({
    mutationFn: () =>
      oauthAppsApi.upsert({
        provider,
        name: name.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
      }),
    onSuccess: () => {
      setError(null);
      onCreated();
      onOpenChange(false);
    },
    onError: (err: Error) => setError(err.message ?? "Save failed"),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!provider) {
      setError("Pick a provider");
      return;
    }
    if (!name.trim() || !clientId.trim() || !clientSecret.trim()) {
      setError("Name, Client ID, and Client Secret are required");
      return;
    }
    save.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New OAuth app</DialogTitle>
          <DialogDescription>
            Register the callback URL upstream, then paste your client ID
            and secret. Secret is stored encrypted.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={onSubmit}
          className="space-y-4 py-2"
          autoComplete="off"
        >
          {/* Decoy field to keep autofill off. */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            value=""
            readOnly
            className="hidden"
            tabIndex={-1}
            aria-hidden="true"
          />

          <div className="space-y-2">
            <Label>Authorized redirect URI</Label>
            <CallbackUrl url={callbackUrl} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="oauth-app-provider">Provider</Label>
            <div className="flex items-center gap-2">
              {selectedProvider && (
                <ProviderLogo provider={selectedProvider} size={24} />
              )}
              <select
                id="oauth-app-provider"
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-base disabled:cursor-not-allowed disabled:opacity-50"
                disabled={save.isPending || availableProviders.length === 0}
              >
                <option value="" disabled>
                  Pick a provider
                </option>
                {availableProviders.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="oauth-app-name">Name</Label>
            <Input
              id="oauth-app-name"
              name="oauth-app-name"
              type="text"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={`e.g. Production ${selectedProvider?.name ?? "OAuth"}`}
              disabled={save.isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="oauth-app-client-id">Client ID</Label>
            <Input
              id="oauth-app-client-id"
              name="oauth-app-client-id"
              type="text"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="From your OAuth app's developer console"
              disabled={save.isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="oauth-app-client-secret">Client Secret</Label>
            <Input
              id="oauth-app-client-secret"
              name="oauth-app-client-secret"
              type="password"
              autoComplete="new-password"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder="Stored encrypted; we never log it"
              disabled={save.isPending}
            />
          </div>

          {error && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={save.isPending || availableProviders.length === 0}
          >
            {save.isPending ? "Saving…" : "Save app"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
